import React, { useState } from 'react';
import { Button, Dimmer, Grid, Icon, Input, Loader, Message, Modal } from 'semantic-ui-react';
import moment from 'moment-timezone';

import { FieldLabel } from '../../../../styles';
import { issueConsultativePolicy } from '../../../../services/fire-insurance-quotation';
import { postFileAndGetPreSignAxios } from '../../../../services/file';
import { sanitizeFilename } from '../../../../utils/fileUtils';
import {
  dangerNotification,
  successNotification,
  warningNotification
} from '../../../../services/notification';

type ConsultativeIssueModalProps = {
  open: boolean;
  /** Item da listagem de apólices (policy com fireInsuranceAnalysis/fireInsurance). */
  item: any;
  onClose: () => void;
  onSaved: () => void;
};

const toDateInput = (value?: string | Date | null): string => {
  if (!value) {
    return '';
  }
  const date = moment(value);
  return date.isValid() ? date.format('YYYY-MM-DD') : '';
};

const errorMessage = (error: any): string => {
  const message = error?.response?.data?.message ?? error?.message;
  if (Array.isArray(message)) {
    return message.join(' ');
  }
  return message || 'Erro inesperado. Tente novamente.';
};

/** Backoffice: conclui a emissão manual de uma apólice de orçamento consultivo. */
const ConsultativeIssueModal = ({ open, item, onClose, onSaved }: ConsultativeIssueModalProps) => {
  const analysis = item.fireInsuranceAnalysis;
  const fireInsurance = analysis?.fireInsurance || item.fireInsurance;

  const [saving, setSaving] = useState<boolean>(false);
  const [policyNumber, setPolicyNumber] = useState<string>('');
  const [externalPolicyCode, setExternalPolicyCode] = useState<string>('');
  const [policyStart, setPolicyStart] = useState<string>(toDateInput(item.policyPeriodStart));
  const [policyEnd, setPolicyEnd] = useState<string>(toDateInput(item.policyPeriodEnd));
  const [policyFile, setPolicyFile] = useState<File | null>(null);

  const handleIssue = async () => {
    if (!policyNumber.trim() || !policyStart || !policyEnd || !policyFile) {
      warningNotification(
        'Dados incompletos',
        'Informe o número da apólice, a vigência e anexe o PDF da apólice.'
      );
      return;
    }

    if (moment(policyEnd).isSameOrBefore(moment(policyStart))) {
      warningNotification('Vigência inválida', 'O fim da vigência deve ser posterior ao início.');
      return;
    }

    setSaving(true);
    try {
      const presign = await postFileAndGetPreSignAxios({
        filename: sanitizeFilename(policyFile.name),
        contentType: policyFile.type || 'application/pdf',
        referencedTableName: 'fire_insurance',
        referencedTableId: analysis.fireInsuranceId,
        name: 'FIRE_INSURANCE_POLICY'
      });

      const upload = await fetch(presign.uploadURL, { method: 'PUT', body: policyFile });
      if (!upload.ok) {
        throw new Error(`Falha no upload do PDF (${upload.status}).`);
      }

      await issueConsultativePolicy(analysis.id, {
        policyFileId: presign.id,
        policyNumber: policyNumber.trim(),
        externalPolicyCode: externalPolicyCode.trim() || undefined,
        policyPeriodStart: policyStart,
        policyPeriodEnd: policyEnd
      });

      successNotification('Sucesso!', 'Apólice emitida. A imobiliária foi avisada por e-mail.');
      onSaved();
      onClose();
    } catch (error) {
      dangerNotification('Erro ao emitir apólice', errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="small" closeOnDimmerClick={false}>
      <Modal.Header>
        <Icon name="check circle" />
        Emitir apólice consultiva
      </Modal.Header>

      <Modal.Content>
        <Dimmer active={saving} inverted>
          <Loader />
        </Dimmer>

        <Message info size="small">
          Contrate junto à Yelum e informe os dados da apólice. Ao emitir, a apólice sai de
          "Aguardando Apólice" e a imobiliária é avisada por e-mail.
        </Message>

        <Grid>
          <Grid.Row>
            <Grid.Column width={8}>
              <FieldLabel>Inquilino</FieldLabel>
              <Input fluid disabled value={fireInsurance?.tenantName || ''} />
            </Grid.Column>
            <Grid.Column width={8}>
              <FieldLabel>Atividade exercida no imóvel</FieldLabel>
              <Input fluid disabled value={fireInsurance?.propertyActivity || ''} />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>
              <FieldLabel required>Número da apólice</FieldLabel>
              <Input
                fluid
                disabled={saving}
                value={policyNumber}
                onChange={(_, { value }) => setPolicyNumber(String(value))}
              />
            </Grid.Column>
            <Grid.Column width={8}>
              <FieldLabel>Código externo (opcional)</FieldLabel>
              <Input
                fluid
                disabled={saving}
                value={externalPolicyCode}
                onChange={(_, { value }) => setExternalPolicyCode(String(value))}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={8}>
              <FieldLabel required>Início da vigência</FieldLabel>
              <Input
                fluid
                type="date"
                disabled={saving}
                value={policyStart}
                onChange={(_, { value }) => setPolicyStart(String(value))}
              />
            </Grid.Column>
            <Grid.Column width={8}>
              <FieldLabel required>Fim da vigência</FieldLabel>
              <Input
                fluid
                type="date"
                disabled={saving}
                value={policyEnd}
                onChange={(_, { value }) => setPolicyEnd(String(value))}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16}>
              <FieldLabel required>PDF da apólice</FieldLabel>
              <input
                type="file"
                accept="application/pdf"
                disabled={saving}
                onChange={event => setPolicyFile(event.target.files?.[0] || null)}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Modal.Content>

      <Modal.Actions>
        <Button disabled={saving} onClick={onClose}>
          Fechar
        </Button>
        <Button positive disabled={saving} onClick={handleIssue}>
          <Icon name="check circle" /> Emitir apólice
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ConsultativeIssueModal;
